import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { VictoryAxis, VictoryChart, VictoryLine, VictoryTheme } from 'victory';

import { CreateAnnualReport } from '../../models';
import { currencyFormatter } from '../../utils';
import { chartColors, Legend } from '../Graph';

interface ChartData {
  x: number;
  y: number;
}

type EquityTableProps = CreateAnnualReport;

export function EquityCharts({
  homeValues,
  equities,
  principalsRemaining,
  equityLessCashInvested,
  cumulativeCashFlowForLoanTerm,
  profitsAtSale,
  totalReturns
}: EquityTableProps) {
  const [homeValuesData, setHomeValuesData] = useState<ChartData[]>([]);
  const [equitiesData, setEquitiesData] = useState<ChartData[]>([]);
  const [principalRemaingData, setPrincipalRemaingData] = useState<ChartData[]>([]);
  const [equityLessCashData, setEquityLessCashData] = useState<ChartData[]>([]);

  const [cumulativeCashFlowData, setCumulativeCashFlowData] = useState<ChartData[]>([]);
  const [profitsAtSaleData, setProfitsAtSaleData] = useState<ChartData[]>([]);
  const [totalReturnsData, setTotalReturnsData] = useState<ChartData[]>([]);

  useEffect(() => {
    setHomeValuesData(homeValues.map((value, i) => ({ x: i + 1, y: value })));
    setEquitiesData(equities.map((value, i) => ({ x: i + 1, y: value })));
    setPrincipalRemaingData(principalsRemaining.map((value, i) => ({ x: i + 1, y: value })));
    setEquityLessCashData(equityLessCashInvested.map((value, i) => ({ x: i + 1, y: value })));

    setCumulativeCashFlowData(
      cumulativeCashFlowForLoanTerm.map((value, i) => ({ x: i + 1, y: value }))
    );
    setProfitsAtSaleData(profitsAtSale.map((value, i) => ({ x: i + 1, y: value })));
    setTotalReturnsData(totalReturns.map((value, i) => ({ x: i + 1, y: value })));
  }, [
    homeValues,
    equities,
    principalsRemaining,
    equityLessCashInvested,
    cumulativeCashFlowForLoanTerm,
    profitsAtSale,
    totalReturns
  ]);

  return (
    <>
      <Flex justify="center" align="center" fontFamily="Montserrat" color="#5A5A5A" mb="40">
        <Box h="xl" w="full">
          <VStack>
            <Text fontSize="2xl" fontWeight="bold">
              Equity Calculations
            </Text>
            <Legend
              labels={['Home Value', 'Equity', 'Principal Remaining', 'Equity Less Cash Invested']}
            />
          </VStack>
          <VictoryChart domainPadding={{ x: 5, y: 10 }} width={650} theme={VictoryTheme.material}>
            <VictoryAxis
              label={'Year'}
              style={{
                tickLabels: {
                  fill: '#5A5A5A',
                  fontFamily: 'Montserrat'
                },
                axisLabel: { padding: 30, fontFamily: 'Montserrat' }
              }}
            />
            <VictoryAxis
              dependentAxis
              label={'Amount'}
              tickFormat={(t) => `${currencyFormatter(t)}`}
              style={{
                tickLabels: {
                  fill: '#5A5A5A',
                  fontFamily: 'Montserrat'
                },
                axisLabel: { padding: 110, fontFamily: 'Montserrat' }
              }}
            />
            <VictoryLine
              data={homeValuesData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[0]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
            <VictoryLine
              data={equitiesData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[1]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
            <VictoryLine
              data={principalRemaingData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[2]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
            <VictoryLine
              data={equityLessCashData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[3]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
          </VictoryChart>
        </Box>
      </Flex>
      <Flex justify="center" align="center" fontFamily="Montserrat" color="#5A5A5A" mb="40">
        <Box h="xl" w="full">
          <VStack>
            <Text fontSize="2xl" fontWeight="bold">
              Equity Calculations (continued)
            </Text>
            <Legend
              labels={['Cumulative Cashflow', 'Profit at Sale', 'Total Return (Cashflow + Equity)']}
            />
          </VStack>
          <VictoryChart domainPadding={{ x: 5, y: 10 }} width={650} theme={VictoryTheme.material}>
            <VictoryAxis
              label={'Year'}
              style={{
                tickLabels: {
                  fill: '#5A5A5A',
                  fontFamily: 'Montserrat'
                },
                axisLabel: { padding: 30, fontFamily: 'Montserrat' }
              }}
            />
            <VictoryAxis
              dependentAxis
              label={'Amount'}
              tickFormat={(t) => `${currencyFormatter(t)}`}
              style={{
                tickLabels: {
                  fill: '#5A5A5A',
                  fontFamily: 'Montserrat'
                },
                axisLabel: { padding: 110, fontFamily: 'Montserrat' }
              }}
            />
            <VictoryLine
              data={cumulativeCashFlowData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[0]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
            <VictoryLine
              data={profitsAtSaleData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[1]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
            <VictoryLine
              data={totalReturnsData}
              interpolation="natural"
              style={{
                data: { stroke: `${chartColors[2]}` },
                parent: { fontFamily: 'Montserrat', fill: '#5A5A5A' }
              }}
            />
          </VictoryChart>
        </Box>
      </Flex>
    </>
  );
}

export default EquityCharts;
